import React from "react";
import ClubBadge from "../ClubBadge";
import { getClubBadge } from "../../data/clubBadges";
import { getFlagUrl } from "../../data/nationalityFlags";


interface Props {
  player: { id: string; name: string; club: string; position: string | null; nationality?: string | null };
  stats: { goals: number; assists: number };
  currentGwStats?: { goals: number; assists: number };
  showAssists: boolean;
  hideStats?: boolean;
  canRemove?: boolean;
  onRemove?: () => void;
}

export default function PlayerCard({
  player, stats, currentGwStats, showAssists, hideStats, canRemove, onRemove,
}: Props) {
  const config = getClubBadge(player.club);
  const primary = config?.primary ?? "#888";
  const gwGoals = currentGwStats?.goals ?? 0;
  const gwAssists = currentGwStats?.assists ?? 0;

  return (
    <div style={{
      minWidth: 0,
      minHeight: canRemove ? 190 : 155,
      background: "var(--color-surface)",
      border: "1px solid var(--color-border-subtle)",
      borderRadius: 12,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    }}>
      {/* Club colour bar */}
      <div style={{ height: 4, background: primary, flexShrink: 0 }} />

      <div style={{ padding: "1rem 1rem", flex: 1, display: "flex", flexDirection: "column", gap: "0.4rem" }}>
        {/* Club + position row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", minWidth: 0 }}>
            <ClubBadge config={config} size={20} title={player.club} />
            <span style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-dim)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {player.club}
            </span>
          </div>
          {player.position && (
            <span style={{
              fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
              padding: "2px 6px", borderRadius: 4,
              background: "var(--color-surface-alt)", color: "var(--color-text-muted)",
              border: "1px solid var(--color-border-subtle)", flexShrink: 0, marginLeft: "0.35rem",
            }}>
              {player.position}
            </span>
          )}
        </div>

        {/* Player name */}
        <div
          style={{
            fontSize: "1.15rem",
            fontWeight: 800,
            color: "var(--color-text)", lineHeight: 1.2,
            fontFamily: "var(--font-display)",
            marginTop: "0.15rem",
          }}
        >
          {player.name}
        </div>

        {/* Nationality flag (shown when hideStats) */}
        {hideStats && player.nationality && (() => {
          const flagUrl = getFlagUrl(player.nationality);
          return flagUrl ? (
            <img src={flagUrl} alt={player.nationality} title={player.nationality} style={{ width: 22, height: "auto", borderRadius: 2, display: "block" }} />
          ) : (
            <span style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>{player.nationality}</span>
          );
        })()}

        {/* Stats row */}
        {!hideStats && (
          <div style={{ display: "flex", gap: "1rem", marginTop: "auto", paddingTop: "0.4rem" }}>
            <div>
              <div style={{ display: "flex", alignItems: "baseline", gap: "0.2rem" }}>
                <span style={{ fontFamily: "var(--font-display)", fontSize: "2rem", fontWeight: 700, lineHeight: 1, color: "var(--color-text)" }}>
                  {stats.goals}
                </span>
                {gwGoals > 0 && (
                  <span style={{ fontSize: "0.65rem", fontWeight: 700, color: "var(--color-success)" }}>+{gwGoals}</span>
                )}
              </div>
              <div style={{ fontSize: "0.58rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-dim)" }}>G</div>
            </div>
            {showAssists && (
              <div>
                <div style={{ display: "flex", alignItems: "baseline", gap: "0.2rem" }}>
                  <span style={{ fontFamily: "var(--font-display)", fontSize: "2rem", fontWeight: 700, lineHeight: 1, color: "var(--color-text)" }}>
                    {stats.assists}
                  </span>
                  {gwAssists > 0 && (
                    <span style={{ fontSize: "0.65rem", fontWeight: 700, color: "var(--color-success)" }}>+{gwAssists}</span>
                  )}
                </div>
                <div style={{ fontSize: "0.58rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-dim)" }}>A</div>
              </div>
            )}
          </div>
        )}

        {/* Remove button */}
        {canRemove && onRemove && (
          <button
            onClick={onRemove}
            style={{
              marginTop: "auto",
              background: "none",
              border: "1px solid var(--color-danger)",
              borderRadius: 8,
              padding: hideStats ? "0.45rem 0.5rem" : "0.35rem 0.5rem",
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "var(--color-danger)",
              cursor: "pointer",
              fontFamily: "var(--font-body)",
              width: "100%",
            }}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
}

export function EmptyPlayerCard({ slotNumber, onAdd }: { slotNumber?: number; onAdd?: () => void }) {
  return (
    <div
      onClick={onAdd}
      style={{
        minWidth: 0,
        background: "transparent",
        border: "1px dashed var(--color-border)",
        borderRadius: 12,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "1.5rem 0.75rem",
        minHeight: 190,
        cursor: onAdd ? "pointer" : "default",
        gap: "0.5rem",
      }}
    >
      {onAdd ? (
        <>
          <span style={{
            width: 36, height: 36, borderRadius: "50%",
            background: "var(--color-surface-alt)", border: "1px solid var(--color-border)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "1.2rem", color: "var(--color-text-muted)",
          }}>+</span>
          {slotNumber != null && (
            <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--color-text-muted)" }}>Slot {slotNumber}</span>
          )}
          <span style={{ fontSize: "0.72rem", color: "var(--color-text-dim)", textAlign: "center", lineHeight: 1.5 }}>
            Pick a player<br />from the pool below
          </span>
        </>
      ) : (
        <span style={{ fontSize: "0.8rem", color: "var(--color-text-dim)", fontStyle: "italic" }}>Empty slot</span>
      )}
    </div>
  );
}
